import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"

import { DAY_LABEL_KEYS, DEMAND_THRESHOLDS } from "../constants/heatmap"
import type { HeatmapGridProps, WeekStartDay } from "../types/heatmap"
import { DEMAND_LEVEL_COLORS, getCellColorClass } from "../utils/heatmap"

// Map weekStart values to their day index (Monday = 0, Sunday = 6)
const DAY_INDEX_MAP: Record<WeekStartDay, number> = {
  monday: 0,
  saturday: 5,
  sunday: 6,
}

/**
 * Reorder array starting from a given index
 */
function reorderFromIndex<T>(arr: T[], startIndex: number): T[] {
  return [...arr.slice(startIndex), ...arr.slice(0, startIndex)]
}

/**
 * Get day label keys ordered by weekStart
 */
function getOrderedDayLabelKeys(weekStart: WeekStartDay): string[] {
  const startIndex = DAY_INDEX_MAP[weekStart]
  return reorderFromIndex([...DAY_LABEL_KEYS], startIndex)
}

export function HeatmapSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex gap-1 mb-2 ml-24">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="flex-1 h-4 bg-[var(--color-border)]/60 rounded" />
        ))}
      </div>
      {Array.from({ length: 7 }).map((_, row) => (
        <div key={row} className="flex gap-1 mb-1">
          <div className="w-24 h-8 bg-[var(--color-border)]/70 rounded" />
          {Array.from({ length: 12 }).map((_, col) => (
            <div key={col} className="flex-1 h-8 bg-[var(--color-border)]/50 rounded" />
          ))}
        </div>
      ))}
    </div>
  )
}

function HeatmapLegend({ maxValue }: { maxValue: number }) {
  const { t } = useTranslation()

  // Convert normalized threshold (0-7 scale) to absolute value
  const getAbsoluteRange = (min: number, max: number): string => {
    const minVal = Math.round((min / 7) * maxValue)
    const maxVal = max === Infinity ? maxValue : Math.round((max / 7) * maxValue)
    if (max === Infinity) return `${minVal}+`
    return `${minVal}-${maxVal}`
  }

  return (
    <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-[var(--color-border)]">
      {DEMAND_THRESHOLDS.map((threshold) => (
        <div key={threshold.level} className="flex items-center gap-2">
          <div className={cn("w-4 h-4 rounded", DEMAND_LEVEL_COLORS[threshold.level])} />
          <span className="body-xs text-[var(--color-text-secondary)]">
            {t(threshold.labelKey)} ({getAbsoluteRange(threshold.min, threshold.max)})
          </span>
        </div>
      ))}
    </div>
  )
}

export function HeatmapGrid({ data, isLoading = false, weekStart = "monday" }: HeatmapGridProps) {
  const { t } = useTranslation()

  if (isLoading || !data) {
    return <HeatmapSkeleton />
  }

  // Get day labels ordered by weekStart (backend already orders the data)
  const orderedDayKeys = getOrderedDayLabelKeys(weekStart)

  // Use time labels from data, or generate default ones from cells
  const timeLabels =
    data.timeLabels ??
    data.cells[0]?.map(
      (cell) =>
        `${String(cell.hour).padStart(2, "0")}:${String(cell.minute ?? 0).padStart(2, "0")}`,
    ) ??
    []

  // Calculate max value for color normalization
  // Use provided maxValue if available, otherwise calculate from cells
  const maxValue =
    data.maxValue ??
    Math.max(
      ...data.cells.flatMap((row) => row.map((cell) => cell.value)),
      1, // Prevent division by zero
    )

  // Convert absolute value to 0-7 scale for color calculation
  const getNormalizedValue = (value: number): number => {
    if (maxValue === 0) return 0
    return Math.round((value / maxValue) * 7)
  }

  // Calculate minimum width based on number of time slots
  // Each cell needs at least 40px to show labels properly
  const minCellWidth = 40
  const gridMinWidth = timeLabels.length * minCellWidth + 96 + timeLabels.length // 96px for day label, gaps

  return (
    <div className="stack-md">
      <div className="relative w-full overflow-visible">
        <div style={{ minWidth: `${Math.max(640, gridMinWidth)}px` }}>
          {/* Time headers row */}
          <div className="flex">
            <div className="w-24 shrink-0" />
            <div className="flex flex-1 gap-1">
              {timeLabels.map((label, index) => (
                <div
                  key={index}
                  className="flex-1 text-center body-xs font-medium text-[var(--color-text-secondary)] pb-2"
                  style={{ minWidth: `${minCellWidth}px` }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Grid rows - data is already ordered by backend based on weekStart */}
          {orderedDayKeys.map((dayKey, rowIndex) => {
            // Check if day is "off" (all cells have 0 value)
            const rowCells = data.cells[rowIndex] ?? []
            const isDayOff = rowCells.length > 0 && rowCells.every((cell) => cell.value === 0)

            return (
              <div key={dayKey} className="flex gap-1 mb-1">
                <div
                  className={cn(
                    "w-24 shrink-0 flex items-center body-sm",
                    isDayOff
                      ? "text-[var(--color-text-secondary)] line-through decoration-2"
                      : "text-[var(--color-text-primary)]",
                  )}
                >
                  {t(dayKey)}
                </div>
                <div className="flex flex-1 gap-1">
                  {rowCells.map((cell, colIndex) => {
                    // Use normalized value (0-7) for color calculation
                    const normalizedValue = getNormalizedValue(cell.value)
                    // Display actual call count
                    const displayValue = Math.round(cell.value)
                    const timeLabel = timeLabels[colIndex] ?? ""

                    return (
                      <div
                        key={`${rowIndex}-${colIndex}`}
                        className={cn(
                          "group/cell flex-1 h-8 rounded flex items-center justify-center",
                          "body-xs font-medium cursor-default",
                          isDayOff
                            ? "bg-[var(--color-border)]/50 bg-[repeating-linear-gradient(135deg,transparent,transparent_3px,var(--color-text-muted)_3px,var(--color-text-muted)_4px)]"
                            : getCellColorClass(normalizedValue),
                        )}
                        style={{ minWidth: `${minCellWidth}px` }}
                        title={
                          isDayOff
                            ? `${t(dayKey)} - ${t("heatmap.dayOff")}`
                            : `${t(dayKey)} ${timeLabel}: ${displayValue}`
                        }
                      >
                        {!isDayOff && (
                          <span className="opacity-0 group-hover/cell:opacity-100 transition-opacity text-[var(--color-text-primary)] bg-[var(--color-surface)]/90 px-1.5 rounded body-xs font-medium">
                            {displayValue}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <HeatmapLegend maxValue={maxValue} />
    </div>
  )
}
